/**
 * `markdownSpeech.ts` — Markdown rendered as a line to SPEAK.
 *
 * The sibling of `MarkdownView.vue` over the same parser (`markdown.ts`): that
 * one turns the AST into DOM, this one turns it into plain text. One caller
 * today — a workflow script's `say` step, on its way to `INTERNAL SAY:`.
 *
 * ## Why this exists at all
 *
 * A `say` line used to reach the realtime model with its markup intact, on the
 * reasoning that the recipient is a language model rather than a TTS engine, so
 * it would read `**straight**` as emphasis. **That was wrong, and it was wrong
 * for a reason inside our own prompt** (user-reported, 2026-08-31: "`**part**`
 * is said as - asterix, asterix, part, asterix, asterix"). `base.md`'s Internal
 * Speech Messages section is one of its few CRITICAL rules and it says: *say
 * exactly the text that follows it verbatim, including punctuation and casing*,
 * do not paraphrase. A model obeying that has been told the asterisks ARE the
 * line. So it sometimes spoke them — and "sometimes" is what a probabilistic
 * reading of two rules pulling against each other looks like.
 *
 * ⚠ **The fix is deliberately NOT a prompt rule**, and re-adding one would
 * weaken the thing it is trying to help. "Speak it verbatim, but silently drop
 * some characters" is a carve-out in the one contract that guarantees an
 * author's words reach the learner unaltered, it is exactly the conflicting
 * always/never that `gpt-realtime-2.x` penalises (see base.md's Preambles note),
 * and it would still only be probable. Handing the model text with no markup in
 * it needs no exception: verbatim stays absolute, because what we give it is
 * already what it should say.
 *
 * ## What survives, and what does not
 *
 * The rule is ONE sentence: **the words survive, the markers do not.** Nothing
 * is dropped except constructs that have no spoken form at all.
 *
 *   - `**bold**`, `*italic*`, `~~struck~~`, `` `code` `` → their inner text.
 *     Struck-through text is SPOKEN rather than skipped: the panel shows it, and
 *     a surface that silently omits a sentence the learner can read is worse
 *     than one that reads a sentence they can see is struck out.
 *   - `[label](https://…)` → the label alone. A URL read aloud is noise, and it
 *     is the one part of a link a learner can do nothing with by ear.
 *   - A `- ` bullet loses its marker entirely (it has no spoken form), while an
 *     ordered item KEEPS its number — in an instruction list the ordinal is
 *     content. It is renumbered from 1 per list, exactly as the panel's `<ol>`
 *     renumbers, so a `10.` typed as the first item is heard as "one" and SEEN
 *     as "1." — wrong in the same way in both places, which is what makes an
 *     author notice and fix it.
 *   - A `---` rule contributes nothing. A block of only rules therefore speaks
 *     nothing at all, which is why the caller must test the RESULT for
 *     emptiness rather than the source.
 *   - Fenced code and a leading `---` frontmatter block keep their text
 *     verbatim, un-parsed. Reading either aloud is odd; dropping the author's
 *     words is worse.
 *   - A table becomes one line per row, cells joined by `, `.
 *
 * Structure becomes line breaks — items one per line, blocks separated by a
 * blank line — and no punctuation is invented to end them. A model reading a
 * list phrases the breaks; adding a period the author did not write would be
 * putting words in their mouth, which is the same sin as speaking the asterisks.
 *
 * ## Two consequences worth knowing before "fixing" them
 *
 * Both are inherited from the dialect, and in both the PANEL already does the
 * same thing — which is the point: the learner reads and hears one lesson, and a
 * mangle they can see is one they can correct.
 *
 *   - `2 * 3 * 4` reads as italic `3` and loses its asterisks. Only PAIRED
 *     markers are claimed, so a lone `*` (`5*`, `2 * 3`) is literal.
 *   - `C:\path` loses its backslash, which is Markdown escaping doing its job.
 *
 * ⚠ Pure and DOM-free. It must not learn about workflows, sessions, or the AI
 * layer: it takes a string and returns a string.
 */

import { parseMarkdownBlocks, parseMarkdownInline, type MdBlock, type MdInline } from "./markdown";

/**
 * The plain-text, speakable form of a Markdown string.
 *
 * Prose carrying no markup comes back UNCHANGED (bar surrounding whitespace):
 * every inline marker is paired-only and a run of plain text is one node, so
 * there is nothing for the pass to claim. Returns `""` for a source that is
 * blank or made only of constructs with no spoken form — callers that must not
 * speak an empty line test THIS, not their input.
 */
export function markdownToSpeechText(source: string): string {
  const spoken = blocksToText(parseMarkdownBlocks(source), "\n\n");
  // Blocks that speak nothing (a `---` rule, an empty item) leave their
  // separator behind; collapse the gaps rather than letting them accumulate.
  return spoken.replace(/\n{3,}/g, "\n\n").trim();
}

function blocksToText(blocks: readonly MdBlock[], separator: string): string {
  return blocks
    .map((block) => blockToText(block))
    .filter((text) => text.length > 0)
    .join(separator);
}

function blockToText(block: MdBlock): string {
  switch (block.type) {
    case "frontmatter":
    case "code":
      // Verbatim: the parser did not scan these for inline markers either, so
      // re-reading them here would find markup the panel never rendered.
      return block.text;
    case "heading":
    case "paragraph":
      return inlineToText(parseMarkdownInline(block.text));
    case "rule":
      return "";
    case "quote":
      return blocksToText(block.blocks, "\n\n");
    case "list":
      return block.items
        .map((item, index) => {
          // An item's own blocks are joined tightly — a paragraph and the nested
          // list under it are one spoken item, not two.
          const text = blocksToText(item, "\n");
          if (text.length === 0) return "";
          return block.ordered ? `${index + 1}. ${text}` : text;
        })
        .filter((text) => text.length > 0)
        .join("\n");
    case "table":
      return [block.head, ...block.rows]
        .map((row) => row.map((cell) => inlineToText(parseMarkdownInline(cell))).join(", "))
        .filter((row) => row.trim().length > 0)
        .join("\n");
  }
}

function inlineToText(nodes: readonly MdInline[]): string {
  let out = "";
  for (const node of nodes) {
    switch (node.type) {
      case "text":
        out += node.text;
        break;
      case "break":
        out += "\n";
        break;
      case "code":
        out += node.text;
        break;
      case "strong":
      case "em":
      case "del":
        out += inlineToText(node.children);
        break;
      case "link":
        // The label only. `href` is never read here — a spoken URL is noise, and
        // this renderer creates no attribute for a scheme to matter in.
        out += inlineToText(node.children);
        break;
    }
  }
  return out;
}
